import React, { createContext, useCallback, useContext, useMemo, useLayoutEffect, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './AuthContext';
import { appTheme, brandColors } from '../theme/appTheme';
import { brandCss } from '../theme/brandCss';
import type { CompanyTheme } from '../types';

export interface BrandColors {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  sidebar: string;
  sidebarDark: string;
  accent: string;
  green50: string;
  green100: string;
  gradientPrimary: string;
  primaryRgb: string;
  sidebarRgb: string;
  sidebarIsLight: boolean;
  tableColor: string;
  tableHeaderText: string;
  formHeaderColor: string;
  formHeaderText: string;
  formHeaderGradient: string;
  isTenantBranded: boolean;
}

interface ThemeContextType {
  themeSettings: typeof brandColors;
  brand: BrandColors;
  previewTheme: (theme: Partial<CompanyTheme> | null) => void;
}

function sanitizeHex(hex: string | undefined, fallback: string): string {
  const raw = String(hex || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return fallback;
}

function shadeColor(hex: string, percent: number): string {
  const n = sanitizeHex(hex, '#6221d6').replace('#', '');
  const num = parseInt(n, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + percent));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function hexToRgb(hex: string): string {
  const n = sanitizeHex(hex, '#6221d6').replace('#', '');
  return `${parseInt(n.slice(0, 2), 16)},${parseInt(n.slice(2, 4), 16)},${parseInt(n.slice(4, 6), 16)}`;
}

function isLightHex(hex: string): boolean {
  const n = sanitizeHex(hex, '#000000').replace('#', '');
  const r = parseInt(n.slice(0, 2), 16) / 255;
  const g = parseInt(n.slice(2, 4), 16) / 255;
  const b = parseInt(n.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 0.62;
}

function applyBrandCssVars(brand: BrandColors) {
  const root = document.documentElement;
  const rgb = brand.primaryRgb;
  root.style.setProperty('--brand-primary', brand.primary);
  root.style.setProperty('--brand-primary-dark', brand.primaryDark);
  root.style.setProperty('--brand-primary-light', brand.primaryLight);
  root.style.setProperty('--brand-primary-rgb', rgb);
  root.style.setProperty('--brand-sidebar', brand.sidebar);
  root.style.setProperty('--brand-sidebar-dark', brand.sidebarDark);
  root.style.setProperty('--brand-sidebar-rgb', brand.sidebarRgb);
  root.style.setProperty('--brand-accent', brand.accent);
  root.style.setProperty('--brand-green50', brand.green50);
  root.style.setProperty('--brand-green100', brand.green100);
  root.style.setProperty('--brand-green200', `${brand.primary}33`);
  root.style.setProperty('--brand-gradient-primary', brand.gradientPrimary);
  root.style.setProperty('--brand-gradient-primary-hover', `linear-gradient(135deg, ${brand.primaryDark} 0%, ${shadeColor(brand.primaryDark, -15)} 100%)`);
  root.style.setProperty('--brand-gradient-soft', `linear-gradient(135deg, ${brand.green50} 0%, ${brand.green100} 100%)`);
  root.style.setProperty('--brand-table-border', `${brand.primary}28`);
  root.style.setProperty('--brand-table-border-strong', brand.primary);
  root.style.setProperty('--brand-focus-ring', `0 0 0 3px rgba(${rgb},0.14)`);
  root.style.setProperty('--brand-focus-ring-strong', `0 0 0 3px rgba(${rgb},0.2)`);
  root.style.setProperty('--brand-shadow-md', `0 4px 20px rgba(${rgb},0.08)`);
  root.style.setProperty('--brand-shadow-lg', `0 8px 32px rgba(${rgb},0.12)`);
  root.style.setProperty('--brand-shadow-button', `0 4px 14px rgba(${rgb},0.35)`);
  root.style.setProperty('--brand-shadow-button-lg', `0 6px 20px rgba(${rgb},0.35)`);
  root.style.setProperty('--brand-shadow-icon', `0 8px 24px rgba(${rgb},0.3)`);
  root.style.setProperty('--brand-content-bg', brand.green50);
  root.style.setProperty('--brand-table-header', brand.tableColor);
  root.style.setProperty('--brand-table-header-text', brand.tableHeaderText);
  root.style.setProperty('--brand-form-header', brand.formHeaderColor);
  root.style.setProperty('--brand-form-header-text', brand.formHeaderText);
  root.style.setProperty('--brand-form-header-gradient', brand.formHeaderGradient);
}

function buildMuiBrandOverrides() {
  return {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          color: '#fff',
          background: brandCss.gradientPrimary,
          boxShadow: brandCss.shadowButton,
          '&:hover': { background: brandCss.gradientPrimaryHover, color: '#fff' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: brandColors.border, '&.Mui-checked': { color: brandCss.primary } },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover': { color: 'inherit' },
          '&.Mui-active': {
            color: 'inherit',
            '& .MuiTableSortLabel-icon': { color: 'inherit', opacity: 1 },
          },
          '& .MuiTableSortLabel-icon': { opacity: 0 },
          '&:hover .MuiTableSortLabel-icon': { opacity: 0.85 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'var(--brand-table-header, var(--brand-primary))',
          color: 'var(--brand-table-header-text, #ffffff)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.Mui-focused fieldset': {
            borderColor: brandCss.primary,
            borderWidth: 2,
          },
        },
      },
    },
  };
}

function resolveBrand(theme?: {
  primaryColor?: string;
  sidebarColor?: string;
  accentColor?: string;
  secondaryColor?: string;
  tableColor?: string;
  formHeaderColor?: string;
}): BrandColors {
  const primary = sanitizeHex(theme?.primaryColor, brandColors.primary);
  const primaryDark = shadeColor(primary, -20);
  const primaryLight = sanitizeHex(theme?.accentColor, shadeColor(primary, 40));
  const sidebar = sanitizeHex(theme?.sidebarColor, brandColors.sidebar);
  const sidebarDark = shadeColor(sidebar, -28);
  const tableColor = sanitizeHex(theme?.tableColor, primary);
  const formHeaderColor = sanitizeHex(theme?.formHeaderColor, primary);
  const formHeaderDark = shadeColor(formHeaderColor, -22);
  return {
    primary,
    primaryDark,
    primaryLight,
    sidebar,
    sidebarDark,
    accent: primaryLight,
    green50: `${primary}14`,
    green100: `${primary}22`,
    gradientPrimary: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
    primaryRgb: hexToRgb(primary),
    sidebarRgb: hexToRgb(sidebar),
    sidebarIsLight: isLightHex(sidebar),
    tableColor,
    tableHeaderText: isLightHex(tableColor) ? '#1a1033' : '#ffffff',
    formHeaderColor,
    formHeaderText: isLightHex(formHeaderColor) ? '#1a1033' : '#ffffff',
    formHeaderGradient: `linear-gradient(135deg, ${formHeaderColor} 0%, ${formHeaderDark} 100%)`,
    isTenantBranded: Boolean(theme?.primaryColor || theme?.sidebarColor || theme?.tableColor || theme?.formHeaderColor),
  };
}

const ThemeContext = createContext<ThemeContextType>({
  themeSettings: brandColors,
  brand: resolveBrand(),
  previewTheme: () => undefined,
});

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [preview, setPreview] = useState<Partial<CompanyTheme> | null>(null);
  const tenantTheme = preview || user?.company?.theme;
  const brand = useMemo(() => resolveBrand(tenantTheme), [tenantTheme]);
  const previewTheme = useCallback((theme: Partial<CompanyTheme> | null) => {
    setPreview(theme);
  }, []);

  useLayoutEffect(() => {
    applyBrandCssVars(brand);
  }, [brand]);

  const muiTheme = useMemo(() => {
    try {
      const secondary = sanitizeHex(tenantTheme?.secondaryColor, '');
      return createTheme(appTheme, {
        palette: {
          primary: {
            main: brand.primary,
            dark: brand.primaryDark,
            light: brand.primaryLight,
            contrastText: '#ffffff',
          },
          ...(secondary && {
            secondary: {
              main: secondary,
              contrastText: '#ffffff',
            },
          }),
        },
        components: buildMuiBrandOverrides(),
      });
    } catch (err) {
      console.error('Failed to apply tenant theme, using defaults', err);
      return appTheme;
    }
  }, [tenantTheme, brand]);

  return (
    <ThemeContext.Provider value={{ themeSettings: brandColors, brand, previewTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useAppTheme = () => useContext(ThemeContext);
